import React, { Fragment, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Card, CardContent, Typography } from '@mui/material';
import {
    Container,
    Content,
    CustomTimeLine,
    Title,
    CustomIcon,
    CustomTitle,
    IconArea,
    CustomGrid,
    CustomTimelineDot,
    ContainerLeft,
    ContainerRight,
    CustomTimeLineItem,
    InstaTimeContent,
    ContainerGrid,
    CustomPagination,
	PaginationContainer,
} from './styles';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';

import moment from 'moment';
import { FormatDate } from '../../utils/formatDate';
import { EmptyContainer } from '../../pages/styles';
import { ColorsContext } from "../../Context/ColorsContext"
import { useTranslation } from 'react-i18next';
import { MapView } from '../MapView';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MapViewMinimal } from '../MapViewMinimal';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Divider from '@mui/material/Divider';
import { PauseCircleOutline } from '@mui/icons-material';

export const TimeLine = ({ data, dataTime = null, plotPlans = null, hideTimelineOnlyMap = false }) => {
    
    const INITIAL_CENTER = { lat: 4.60971, lng: -74.08175 };
    const INITIAL_ZOOM = 18;

    const { format } = new FormatDate();
    const { t } = useTranslation();
    const { colors } = React.useContext(ColorsContext);

    const timelineMapRef = useRef(null);
    const [mapCenter, setMapCenter] = useState(INITIAL_CENTER);
    const [mapZoom, setMapZoom] = useState(INITIAL_ZOOM);
    const [activeMarkerId, setActiveMarkerId] = useState(null);
    const [selected, setSelected] = useState(null);

    const [isMapExpanded, setIsMapExpanded] = useState(false);
    const isMapExpandedRef = useRef(false);
    const [mapInitialized, setMapInitialized] = useState(false);

    const contentRef = useRef(null);
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    React.useEffect(() => {
        if (!contentRef.current) return;
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const availableHeight = entry.contentRect.height;
                if (availableHeight > 0) {
                    const estimatedItemHeight = 70; // Estimate height of a timeline item
                    const calcItems = Math.max(3, Math.floor(availableHeight / estimatedItemHeight));
                    setItemsPerPage(prev => {
                        if (isMapExpandedRef.current) return prev;
                        return prev !== calcItems ? calcItems : prev;
                    });
                }
            }
        });
        observer.observe(contentRef.current);
        return () => observer.disconnect();
    }, []);

    const totalItems = (data || []).length;
    const maxPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
    const currentPage = Math.min(page, maxPages);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const paginatedData = (data || []).slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const getInstanceTime = () => {
        if (dataTime !== null) {
            const tempActiveTime = moment.duration(dataTime.activeTime);
            const tempSuspendedTime = moment.duration(dataTime.suspendedTime);

            let textActiveTime = (tempActiveTime.hours() > 0 ? `${tempActiveTime.hours()} h` : '')
            textActiveTime += ' ' + (tempActiveTime.minutes() > 0 ? `${tempActiveTime.minutes()} min` : `${tempActiveTime.seconds()} seg`)

            let textSuspendedTime = (tempSuspendedTime.hours() > 0 ? `${tempSuspendedTime.hours()} h` : ' ')
            textSuspendedTime += ' ' + (tempSuspendedTime.minutes() > 0 ? `${tempSuspendedTime.minutes()} min` : `${tempSuspendedTime.seconds()} seg`)

            return (
                <InstaTimeContent>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px' }}>
                        <AccessTimeIcon sx={{ fontSize: 18, color: colors.grayFont }} />
                        <span style={{ fontSize: 11, color: colors.grayFont, whiteSpace: 'nowrap' }}>{t('workflow.detail.activeTime')}: <strong style={{ fontWeight: 'bold' }}>{textActiveTime}</strong></span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <PauseCircleOutline sx={{ fontSize: 18, color: colors.grayFont }} />
                        <span style={{ fontSize: 11, color: colors.grayFont, whiteSpace: 'nowrap' }}>{t('workflow.detail.suspendedTime')}: <strong style={{ fontWeight: 'bold' }}>{textSuspendedTime}</strong></span>
                    </div>
                </InstaTimeContent>
            )
        }
    }

    const convertToArray = (string, tooltip) => {
        if (!string) return [];

        const stringEmail = string.split(',');
        const array = stringEmail.map(e => e.trim())

        if (tooltip) {
            return array.slice(1);
        }
        return array
    }

    const dateHour = ({ date, hour }) => {
        let result;

        if (date) {
            // Use obj.createdAt if obj.updatedAt is null
            let effectiveDate = date || obj.createdAt;
            result = format({ date: effectiveDate, formatWithoutHour: true });
            return result;
        }

        // Use obj.createdAt if obj.updatedAt is null
        let effectiveHour = hour || obj.createdAt;
        result = format({ date: effectiveHour, hour: true });
        return result;
    };

    const countWords = (wordsString, tooltip) => {

        if (!wordsString) {
            return '';
        }

        wordsString = wordsString.trim();

        if (wordsString.length > 53 && tooltip) {
            const wordsArray = wordsString.split(' ');
            let wordsToShow = Math.ceil(wordsArray.length * (53 / wordsString.length));
            if (wordsToShow === 0) wordsToShow = 1;
            let remainingWords = wordsArray.slice(wordsToShow).join(' ');
            return remainingWords;
        }

        if (wordsString.length > 53) {
            let truncatedString = wordsString.substring(0, 53);
            const lastSpaceIndex = truncatedString.lastIndexOf(' ');
            if (lastSpaceIndex !== -1) {
                truncatedString = truncatedString.substring(0, lastSpaceIndex);
            }
            return truncatedString + '...';
        }

        return wordsString;

    }

    function handleCloseInfo() {
        clearSelection();
    }

    function clearSelection() {
        setActiveMarkerId(null);
        setSelected(null);
    }

    function toggleMarker(item) {

        // Si ya está activo, se desmarca
        if (activeMarkerId === item.id) {
            clearSelection()
            return;
        }

        // Activar marcados
        setActiveMarkerId(item.id);
        setSelected(item);
    }

    const showMap = Array.isArray(plotPlans) && plotPlans.some(p => p.latitude && p.longitude);

    const mapPositions = React.useMemo(() => {
        if (!showMap) return [];

        const newPositions = plotPlans
            ?.filter(p => p.latitude != null && p.longitude != null)
            .map(p => ({
                id: p.id,
                name: p.name ?? p.description ?? `Plot ${p.id}`,
                lat: Number(p.latitude),
                lng: Number(p.longitude),
                workflowDatas: p.workflowDatas ?? [],
                nodeLevel: p.nodeLevel ?? 0,
                raw: p
            })) ?? [];

        if (newPositions.length > 0) setMapCenter({lat: newPositions[0].lat, lng: newPositions[0].lng})

        return newPositions.map((p, index) => ({
            ...p,
            index: index + 1
        }));
    }, [data, showMap]);

    const handleAccordionChange = (event, expanded) => {
        setIsMapExpanded(expanded);
        isMapExpandedRef.current = expanded;

        // Marcar que el mapa debe inicializarse cuando se expande por primera vez (así no genera cobro de api cada vez)
        if (expanded && !mapInitialized) {
            setMapInitialized(true);
        }
    };


    return (
        <Container>
            {!hideTimelineOnlyMap && (
            <Card
                variant="outlined" 
                style={{ 
                    marginBottom: showMap ? '16px' : 0,
                    borderRadius: '10px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    backgroundColor: '#ffffff',
                    height: showMap ? '52%' : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    border: 'none',
                }}
            >
                <CardContent sx={{
                    padding: '0px !important',
                    height: showMap ? '90%' : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                }}>
                    <Title variant="h6" sx={{ padding: '8px 16px' }}>
                        {t('workflow.detail.timeLine')}
                    </Title>
                    <Divider sx={{ borderColor: '#e0e0e0', width: '100%', opacity: 1 }} />
                    <Content ref={contentRef}>
                        <CustomTimeLine>
                            {paginatedData.length > 0 ?
                                <Fragment>
                                    {paginatedData.map((obj) => {
                                        const hasExecutedBy = Boolean(obj.executedByEmail);
                                        const customColor = !hasExecutedBy ? '#b1b1b1' : undefined;

                                        return (
                                        <CustomTimeLineItem key={obj.id}>
                                            <ContainerLeft>
                                                <CustomTitle style={{ color: customColor }}>
                                                    {dateHour({ date: obj.updatedAt })}
                                                </CustomTitle>

                                                <CustomTitle style={{ color: customColor }}>
                                                    {dateHour({ hour: obj.updatedAt })}
                                                    {/* {format({ date: obj.updatedAt, hour: true })} */}
                                                </CustomTitle>

                                            </ContainerLeft>

                                            <TimelineSeparator sx={{ flex: '0 0 10%', minWidth: '32px', alignItems: 'center' }}>
                                                <CustomTimelineDot
                                                    sx={{
                                                        background: !hasExecutedBy ? 'transparent' : (obj.workflowStatus?.color || colors.grayFont),
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        width: '32px',
                                                        border: !hasExecutedBy ? '2px dotted #b1b1b1' : undefined,
                                                        boxShadow: !hasExecutedBy ? 'none' : undefined,
                                                    }}
                                                >
                                                    <CustomIcon style={{ color: !hasExecutedBy ? '#b1b1b1' : undefined }}>{obj.workflowStatus?.icon}</CustomIcon>
                                                </CustomTimelineDot>

                                                <TimelineConnector />

                                            </TimelineSeparator>

                                            <ContainerRight>
                                                <CustomTitle type="title" style={{ fontWeight: 'bold', color: !hasExecutedBy ? '#b1b1b1' : '#686868' }}>
                                                    {obj.workflowStatus.title}
                                                </CustomTitle>

                                                {/* Executed By */}
                                                <>
                                                    <Grid container>
                                                        <CustomGrid xs="auto">
                                                            <CustomTitle style={{ flexShrink: 0, color: customColor }}>
                                                                {t('workflow.detail.ExecutedBy')}:
                                                            </CustomTitle>

                                                            <CustomTitle style={{ color: customColor }}>
                                                                {obj.executedByEmail && obj.executedByEmail}
                                                            </CustomTitle>
                                                        </CustomGrid>
                                                    </Grid>
                                                </>

                                                {/* Notified To */}
                                                {
                                                    obj.notifiedTo !== null ? (
                                                        <>
                                                            <Tooltip title={convertToArray(obj.notifiedTo, true).join(', ')}>
                                                                <ContainerGrid container>
                                                                    <CustomGrid xs="auto">
                                                                        <CustomTitle style={{ flexShrink: 0, color: customColor }}>
                                                                            {t('workflow.detail.NotifiedTo')}:
                                                                        </CustomTitle>

                                                                        <CustomTitle style={{ color: customColor }}>
                                                                            {obj.notifiedTo && convertToArray(obj.notifiedTo)[0] || ''}
                                                                        </CustomTitle>
                                                                    </CustomGrid>

                                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                        {
                                                                            obj.notifiedTo !== null && convertToArray(obj.notifiedTo, true).length > 0 && (
                                                                                <IconArea>{`+${convertToArray(obj.notifiedTo, true).length}`}</IconArea>
                                                                            )
                                                                        }
                                                                    </div>

                                                                </ContainerGrid>
                                                            </Tooltip>
                                                        </>
                                                    ) : (
                                                        <Grid container>
                                                            <CustomGrid xs='auto'>
                                                                <CustomTitle style={{ flexShrink: 0, color: customColor }}>
                                                                    {t('workflow.detail.NotifiedTo')}:
                                                                </CustomTitle>

                                                                <CustomTitle style={{ color: customColor }}>
                                                                    {obj.notifiedTo && convertToArray(obj.notifiedTo)[0] || ''}
                                                                </CustomTitle>
                                                            </CustomGrid>
                                                        </Grid>
                                                    )
                                                }

                                                {/*  reason for rejection */}

                                                {
                                                    obj.workflowDataCancellationReason !== null &&
                                                    <Grid container>
                                                        <CustomGrid xs="auto" fixWidth={true}>
                                                            <CustomTitle type="title" style={{ color: customColor }}>
                                                                {t('workflow.detail.reasonRejection')}:
                                                                <Tooltip title={countWords(obj.workflowDataCancellationReason.observations || '', true)}>
                                                                    <span style={{ fontWeight: 'normal', marginLeft: '3px', color: customColor }}>
                                                                        {`${obj.workflowDataCancellationReason.workflowStatusCancellationReason.name}
                                                                            ${obj.workflowDataCancellationReason.observations ? ` - ${countWords(obj.workflowDataCancellationReason.observations)}` : ''}
                                                                        `}
                                                                    </span>
                                                                </Tooltip>
                                                            </CustomTitle>
                                                        </CustomGrid>
                                                    </Grid>
                                                }
                                            </ContainerRight >
                                        </CustomTimeLineItem>
                                    )})}
                                </Fragment>   
                            :
                            <EmptyContainer>{t('common.empty.noInfo')}</EmptyContainer>
                        }
                        </CustomTimeLine>
                    </Content>
                    {dataTime !== null && getInstanceTime()}
                    {totalItems > itemsPerPage && (
                        <PaginationContainer>
                            <Typography sx={{ fontSize: '11px', color: colors.grayFont, fontWeight: 500, marginRight: 'auto' }}>
                                {t('workflow.detail.showing')} 
                                {" "} {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)}  
                                {" "} {t('workflow.detail.of')} {totalItems}
                            </Typography>
                            <CustomPagination 
                                count={maxPages} 
                                page={currentPage} 
                                onChange={handlePageChange} 
                                color="primary" 
                                size="small"
                                showFirstButton
                                showLastButton
                                siblingCount={0}
                                boundaryCount={1}
                            />
                        </PaginationContainer>
                    )}
                </CardContent>
            </Card>
            )}

            {showMap && mapPositions.length > 0 && (
              <Card
                variant="outlined" 
                style={{ 
                    borderRadius: '10px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    border: 'none',
                }}
              >
                <Accordion disableGutters sx={{ borderRadius: '10px', '&:before': { display: 'none' }, overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: 'none' }} onChange={handleAccordionChange} >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} variant="outlined"
                        sx={{ 
                            borderRadius: '8px',
                            border: 'none',
                            '&:before': { 
                                display: 'none' 
                            },
                            minHeight: 48,
                            '&.Mui-expanded': {
                                minHeight: 48,
                            },
                            '& .MuiAccordionSummary-content': {
                                margin: '12px 0',
                            },
                            '& .MuiAccordionSummary-content.Mui-expanded': {
                                margin: '12px 0',
                            }
                        }}
                        aria-controls="map-content"
                        id="map-header"
                    >
                        <Title variant="h6">{t('workflow.detail.mapView')}</Title>
                    </AccordionSummary>
                    <AccordionDetails>
                        {mapInitialized && (
                            <Grid item xs={12} 
                                sx={{ 
                                    height: hideTimelineOnlyMap ? 450 : 300, 
                                    minHeight: hideTimelineOnlyMap ? 450 : 300,
                                    borderRadius: '10px',
                                    display: isMapExpanded ? 'block' : 'none'
                                }}
                            >
                                <MapViewMinimal
                                    mapRef={timelineMapRef}
                                    mapCenter={mapCenter}
                                    mapZoom={mapZoom}
                                    tableData={mapPositions}
                                    selected={selected}
                                    activeMarkerId={activeMarkerId}
                                    onMarkerClick={toggleMarker}
                                    onCloseInfo={handleCloseInfo}
                                    // loadWorkflowCount={null}
                                    goToDetail={null}
                                    fitBound={true}
                                />
                            </Grid>
                        )}
                    </AccordionDetails>
                </Accordion>
              </Card>
            )}
        </Container>
    )
}

